import { useState, useEffect } from 'react';
import {
  Award,
  TrendingUp,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';

interface Statistics {
  status: {
    total: number;
    active: number;
    revoked: number;
    expired: number;
  };
  period: {
    name: string;
    issued: number;
  };
  activity: {
    total_downloads: number;
    total_verifications: number;
  };
  top_certificates: Array<{
    certificate_number: string;
    title: string;
    verification_count: number;
    candidate_name: string;
  }>;
  recent_activity: Array<{
    date: string;
    actions: number;
  }>;
}

export function StatisticsWidget() {
  const [stats, setStats] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'all' | 'today' | 'this_week' | 'this_month' | 'this_year'>('this_month');

  useEffect(() => {
    fetchStatistics();
  }, [period]);

  async function fetchStatistics() {
    try {
      setLoading(true);
      const res = await fetch(`/api/admin/certificates/statistics?period=${period}`, {
        credentials: 'include'
      });

      if (!res.ok) {
        throw new Error('Kunne ikke hente statistikk');
      }

      const data = await res.json();
      setStats(data.statistics);
    } catch (err) {
      console.error('Fetch statistics error:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="card py-12 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kf-orange mx-auto"></div>
        <p className="mt-4 text-kf-gray-600">Laster statistikk...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="card text-center py-12">
        <AlertTriangle className="w-16 h-16 text-kf-orange-500 mx-auto mb-4" />
        <p className="text-kf-gray-600">Kunne ikke laste statistikk</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-kf-gray-900">📊 Statistikk</h2>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as any)}
          className="px-4 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-orange focus:border-transparent"
        >
          <option value="today">I dag</option>
          <option value="this_week">Denne uken</option>
          <option value="this_month">Denne måneden</option>
          <option value="this_year">Dette året</option>
          <option value="all">Alle</option>
        </select>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total */}
        <div className="card bg-gradient-to-br from-kf-orange-500 to-kf-orange-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Totalt</p>
              <p className="text-3xl font-bold">{stats.status.total}</p>
            </div>
            <Award className="w-12 h-12 opacity-80" />
          </div>
        </div>

        {/* Active */}
        <div className="card bg-gradient-to-br from-kf-blue-500 to-kf-blue-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Aktive</p>
              <p className="text-3xl font-bold">{stats.status.active}</p>
            </div>
            <CheckCircle className="w-12 h-12 opacity-80" />
          </div>
        </div>

        {/* Revoked */}
        <div className="card bg-gradient-to-br from-kf-info-500 to-kf-info-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Tilbakekalt</p>
              <p className="text-3xl font-bold">{stats.status.revoked}</p>
            </div>
            <XCircle className="w-12 h-12 opacity-80" />
          </div>
        </div>

        {/* Expired */}
        <div className="card bg-gradient-to-br from-kf-warning-500 to-kf-warning-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Utløpt</p>
              <p className="text-3xl font-bold">{stats.status.expired}</p>
            </div>
            <Clock className="w-12 h-12 opacity-80" />
          </div>
        </div>
      </div>

      {/* Activity Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-kf-blue-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-kf-blue-600" />
            </div>
            <div>
              <p className="text-sm text-kf-gray-600">Utstedt ({periodLabel(period)})</p>
              <p className="text-2xl font-bold text-kf-gray-900">{stats.period.issued}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-kf-orange-100 rounded-lg">
              <Eye className="w-6 h-6 text-kf-orange-600" />
            </div>
            <div>
              <p className="text-sm text-kf-gray-600">Totalt nedlastinger</p>
              <p className="text-2xl font-bold text-kf-gray-900">{stats.activity.total_downloads}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-kf-blue-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-kf-blue-600" />
            </div>
            <div>
              <p className="text-sm text-kf-gray-600">Totalt verifikasjoner</p>
              <p className="text-2xl font-bold text-kf-gray-900">{stats.activity.total_verifications}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Certificates */}
      {stats.top_certificates.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-bold text-kf-gray-900 mb-4">🏆 Mest verifiserte attester</h3>
          <div className="space-y-3">
            {stats.top_certificates.map((cert, idx) => (
              <div key={cert.certificate_number} className="flex items-center justify-between py-2 border-b border-kf-gray-200 last:border-0">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                    idx === 0 ? 'bg-kf-orange-500' : idx === 1 ? 'bg-kf-gray-400' : idx === 2 ? 'bg-kf-orange-600' : 'bg-kf-gray-300'
                  }`}>
                    {idx + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-kf-gray-900">{cert.title}</p>
                    <p className="text-sm text-kf-gray-600">{cert.candidate_name} • {cert.certificate_number}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-kf-orange-600">{cert.verification_count}</p>
                  <p className="text-xs text-kf-gray-500">verifikasjoner</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity Chart */}
      {stats.recent_activity.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-bold text-kf-gray-900 mb-4">📈 Aktivitet siste 7 dager</h3>
          <div className="space-y-2">
            {stats.recent_activity.map((day) => {
              const maxActions = Math.max(...stats.recent_activity.map(d => d.actions));
              const percentage = (day.actions / maxActions) * 100;

              return (
                <div key={day.date} className="flex items-center gap-3">
                  <p className="text-sm text-kf-gray-600 w-24">
                    {new Date(day.date).toLocaleDateString('no-NO', { month: 'short', day: 'numeric' })}
                  </p>
                  <div className="flex-1 bg-kf-gray-200 rounded-full h-6 overflow-hidden">
                    <div
                      className="bg-kf-orange-600 h-full flex items-center justify-end pr-2"
                      style={{ width: `${percentage}%` }}
                    >
                      {day.actions > 0 && (
                        <span className="text-xs font-semibold text-white">{day.actions}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function periodLabel(period: string): string {
  const labels: Record<string, string> = {
    today: 'i dag',
    this_week: 'denne uken',
    this_month: 'denne måneden',
    this_year: 'dette året',
    all: 'totalt'
  };
  return labels[period] || period;
}
